import React, { useState } from 'react';
import {
  Box,
  Stack,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  useTheme,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Autocomplete
} from '@mui/material';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';

interface CreateEducatorProfileProps {
  uid: string;
  onComplete: () => void;
  isParentLoading: boolean; // Add prop for parent loading state
}

interface FormData {
  organizationName: string;
  jobTitleSelection: string; // Stores the value from the Select (e.g., "Teacher", "Other")
  otherJobTitleText: string; // Stores the text if "Other" is selected
  state: string | null; // Add state, allow null for initial empty selection
}

interface FormErrors {
  organizationName?: string;
  jobTitleSelection?: string;
  otherJobTitleText?: string;
  state?: string; // Add state error
  submit?: string;
}

const jobTitleOptions = [
  "Teacher",
  "Tutor",
  "Counselor",
  "Principal",
  "Director",
  "Assistant Superintendent",
  "Superintendent",
  "Other"
];

// List of US States
const usStates = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut", "Delaware", "District of Columbia",
  "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky",
  "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota", "Mississippi",
  "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire", "New Jersey", "New Mexico",
  "New York", "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon", "Pennsylvania", "Puerto Rico",
  "Rhode Island", "South Carolina", "South Dakota", "Tennessee", "Texas", "Utah", "Vermont",
  "Virginia", "Washington", "West Virginia", "Wisconsin", "Wyoming", "Other"
  // Territories can be added if needed
  // "Guam", "American Samoa", "U.S. Virgin Islands", "Northern Mariana Islands"
];

const CreateEducatorProfile: React.FC<CreateEducatorProfileProps> = ({ uid, onComplete, isParentLoading }) => {
  const theme = useTheme();
  const [formData, setFormData] = useState<FormData>({
    organizationName: '',
    jobTitleSelection: '',
    otherJobTitleText: '',
    state: null, // Initialize state as null
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.organizationName.trim()) {
      newErrors.organizationName = 'Organization name is required.';
    }
    if (!formData.jobTitleSelection) {
      newErrors.jobTitleSelection = 'Please select a job title.';
    } else if (formData.jobTitleSelection === 'Other' && !formData.otherJobTitleText.trim()) {
      newErrors.otherJobTitleText = 'Please specify your job title.';
    }
    if (!formData.state) { // Validate state
      newErrors.state = 'Please select your state.';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    const name = event.target.name as keyof FormData;
    const value = event.target.value as string;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      // Clear otherJobTitleText if a non-"Other" option is selected
      ...(name === 'jobTitleSelection' && value !== 'Other' && { otherJobTitleText: '' }),
    }));
     if (name === 'jobTitleSelection' && errors.jobTitleSelection) {
      setErrors(prev => ({...prev, jobTitleSelection: undefined}));
    }
  };

  // Handler for Autocomplete change
  const handleStateChange = (event: React.SyntheticEvent, newValue: string | null) => {
    setFormData(prev => ({
      ...prev,
      state: newValue,
    }));
    if (newValue && errors.state) {
      setErrors(prev => ({ ...prev, state: undefined }));
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!validateForm()) {
      return;
    }
    setIsSubmitting(true);
    setErrors({});

    const finalJobTitle = formData.jobTitleSelection === 'Other'
      ? formData.otherJobTitleText.trim()
      : formData.jobTitleSelection;

    const educatorDataToUpdate = {
      organizationName: formData.organizationName.trim(),
      jobTitle: finalJobTitle,
      state: formData.state, // Add state to the data being updated
      educatorProfileCompleted: true,
    };

    try {
      const userRef = doc(db, 'users', uid);
      await updateDoc(userRef, educatorDataToUpdate);
      // TODO: Implement HubSpot Contact creation API call upon submission.
      onComplete();
    } catch (error) {
      console.error("Error updating educator profile:", error);
      let errorMessage = "Failed to update profile. Please try again.";
      if (error instanceof Error) {
          errorMessage = error.message;
      }
      setErrors({ submit: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card sx={{ width: '100%', maxWidth: 500, margin: 'auto', mt: 3, p: { xs: 1, sm: 2 } }}>
      <CardContent>
        <Typography variant="h5" component="h2" gutterBottom textAlign="center">
          Educator Information
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 3 }}>
          Tell us a bit about your role.
        </Typography>
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Stack spacing={2.5}>
          <FormControl fullWidth required error={!!errors.jobTitleSelection}>
              <InputLabel id="jobTitle-select-label">Job Title</InputLabel>
              <Select
                labelId="jobTitle-select-label"
                id="jobTitleSelection"
                name="jobTitleSelection"
                value={formData.jobTitleSelection}
                label="Job Title"
                onChange={handleSelectChange as any} // MUI Select type issue with name
              >
                {jobTitleOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
              {errors.jobTitleSelection && <Typography color="error" variant="caption" sx={{ml:1.5, mt:0.5}}>{errors.jobTitleSelection}</Typography>}
            </FormControl>

            {formData.jobTitleSelection === 'Other' && (
              <TextField
                required
                fullWidth
                id="otherJobTitleText"
                name="otherJobTitleText"
                label="Please specify your job title"
                value={formData.otherJobTitleText}
                onChange={handleInputChange}
                error={!!errors.otherJobTitleText}
                helperText={errors.otherJobTitleText}
                sx={{ mt: 1 }} // Add some margin if it appears
              />
            )}
            
            <TextField
              required
              fullWidth
              id="organizationName"
              name="organizationName"
              label="School / District / Organization Name"
              placeholder="Ex: Davis High School, Grant School District, etc."
              value={formData.organizationName}
              onChange={handleInputChange}
              error={!!errors.organizationName}
              helperText={errors.organizationName}
            />

            <Autocomplete
              id="state-autocomplete"
              options={usStates}
              value={formData.state}
              onChange={handleStateChange}
              isOptionEqualToValue={(option, value) => option === value || value === ""}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="State"
                  required
                  error={!!errors.state}
                  helperText={errors.state}
                />
              )}
              fullWidth
            />
            
            {errors.submit && (
                <Alert severity="error" sx={{ mt: 2 }}>{errors.submit}</Alert>
            )}

            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              disabled={isSubmitting || isParentLoading}
              sx={{ mt: 3, py: 1.5, textTransform: 'none', fontSize: '1rem' }}
            >
              {isSubmitting || isParentLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} sx={{ color: 'white' }} />
                  <span>{isSubmitting ? 'Saving Profile...' : 'Setting up your account...'}</span>
                </Box>
              ) : 'Complete Profile'}
            </Button>
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
};

export default CreateEducatorProfile;